import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import BrandHeader from "../../components/BrandHeader";
import { vedicCalendarApi } from "../../lib/api";

const CHAKRAS = [
  "",
  "Muladhara (Root)",
  "Svadhisthana (Sacral)",
  "Manipura (Solar Plexus)",
  "Anahata (Heart)",
  "Vishuddha (Throat)",
  "Ajna (Third Eye)",
  "Sahasrara (Crown)",
];

const RASHI = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces",
];

const EMPTY_ROW = {
  start_date: "",
  end_date: "",
  transit_name: "",
  affected_naam_ank: [],
  affected_rashi: [],
  affected_chakra: "",
  theme_text: "",
  festival_name: "",
  festival_significance: "",
  verified: true,
  is_active: true,
};

export default function VedicCalendar() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // row being edited (or null)
  const [filter, setFilter] = useState("all"); // all | unverified | inactive
  const [reportModal, setReportModal] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("ph-admin") !== "ok") {
      navigate("/admin", { replace: true });
    }
  }, [navigate]);

  const reload = async () => {
    setLoading(true);
    try {
      const list = await vedicCalendarApi.list({ verified_only: false, active_only: false });
      setRows(list);
    } catch {
      toast.error("Failed to load calendar");
    }
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const filtered = rows.filter((r) => {
    if (filter === "unverified") return !r.verified;
    if (filter === "inactive") return !r.is_active;
    return true;
  });

  const handleSave = async (form) => {
    try {
      if (form.id) {
        const updated = await vedicCalendarApi.update(form.id, form);
        setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
        toast.success("Updated");
      } else {
        const created = await vedicCalendarApi.create(form);
        setRows((rs) => [...rs, created]);
        toast.success("Added");
      }
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this row? This cannot be undone.")) return;
    try {
      await vedicCalendarApi.remove(id);
      setRows((rs) => rs.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleToggleVerify = async (row) => {
    const updated = await vedicCalendarApi.update(row.id, { verified: !row.verified });
    setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
  };

  const handleToggleActive = async (row) => {
    const updated = await vedicCalendarApi.update(row.id, { is_active: !row.is_active });
    setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
  };

  const handleGenerateAll = async (year, month) => {
    setGenerating(true);
    try {
      const res = await vedicCalendarApi.generateReport({ year, month });
      toast.success(`Generated ${res.generated} report${res.generated !== 1 ? "s" : ""} for ${monthName(month)} ${year}`);
      setReportModal(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="vedic-calendar-page">
      <BrandHeader />
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-3xl text-[#2C0A5E]">Vedic Calendar</h1>
            <p className="text-sm text-[#6B5E7E] mt-1">Transits, festivals, and energy windows that drive customer suggestions.</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setReportModal(true)}
              className="ph-btn-ghost"
              data-testid="generate-reports-btn"
            >
              Generate monthly reports
            </button>
            <button
              onClick={() => setEditing({ ...EMPTY_ROW })}
              className="ph-btn-primary"
              data-testid="add-calendar-row"
            >
              + Add row
            </button>
          </div>
        </div>

        <div className="flex gap-2 mt-5 text-xs">
          {[
            { v: "all", label: `All (${rows.length})` },
            { v: "unverified", label: `VERIFY-flagged (${rows.filter((r) => !r.verified).length})` },
            { v: "inactive", label: `Inactive (${rows.filter((r) => !r.is_active).length})` },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              className="px-3 py-1.5 rounded-full"
              style={{
                background: filter === f.v ? "#6A0DAD" : "white",
                color: filter === f.v ? "white" : "#6A0DAD",
                border: "1px solid #6A0DAD",
              }}
              data-testid={`filter-${f.v}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-6 bg-white rounded-xl overflow-x-auto" style={{ border: "1px solid rgba(106,13,173,0.1)" }}>
          {loading ? (
            <div className="p-8 text-center text-[#6B5E7E]">Loading…</div>
          ) : (
            <table className="w-full text-sm" data-testid="calendar-table">
              <thead>
                <tr className="text-left text-[#6B5E7E] bg-[#FAF8F4]">
                  <th className="py-3 px-3">Dates</th>
                  <th className="py-3 px-3">Transit / Festival</th>
                  <th className="py-3 px-3">Tagging</th>
                  <th className="py-3 px-3">Verified</th>
                  <th className="py-3 px-3">Active</th>
                  <th className="py-3 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-[rgba(106,13,173,0.06)]">
                    <td className="py-3 px-3 whitespace-nowrap text-[#2C0A5E]">
                      {r.start_date}
                      {r.end_date && r.end_date !== r.start_date && <span className="block text-[#6B5E7E] text-xs">→ {r.end_date}</span>}
                    </td>
                    <td className="py-3 px-3">
                      <div className="font-medium text-[#2C0A5E]">{r.transit_name || r.festival_name || "—"}</div>
                      <div className="text-xs text-[#6B5E7E] italic max-w-[360px]">{r.theme_text}</div>
                    </td>
                    <td className="py-3 px-3 text-xs text-[#6B5E7E]">
                      {r.affected_naam_ank?.length > 0 && <div>NN: {r.affected_naam_ank.join(", ")}</div>}
                      {r.affected_rashi?.length > 0 && <div>Rashi: {r.affected_rashi.join(", ")}</div>}
                      {r.affected_chakra && <div>Chakra: {r.affected_chakra}</div>}
                    </td>
                    <td className="py-3 px-3">
                      <button onClick={() => handleToggleVerify(r)} className="text-xs px-2 py-1 rounded-full" style={{ background: r.verified ? "#e8f5e9" : "#fff3e0", color: r.verified ? "#1b5e20" : "#e65100" }} data-testid={`verify-toggle-${r.id}`}>
                        {r.verified ? "✓ Verified" : "VERIFY"}
                      </button>
                    </td>
                    <td className="py-3 px-3">
                      <button onClick={() => handleToggleActive(r)} className="text-xs px-2 py-1 rounded-full" style={{ background: r.is_active ? "#e1f5fe" : "#eee", color: r.is_active ? "#01579b" : "#666" }} data-testid={`active-toggle-${r.id}`}>
                        {r.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      <button onClick={() => setEditing(r)} className="text-[#6A0DAD] hover:underline text-xs mr-3" data-testid={`edit-${r.id}`}>Edit</button>
                      <button onClick={() => handleDelete(r.id)} className="text-[#C13B1B] hover:underline text-xs" data-testid={`delete-${r.id}`}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Link to="/admin/dashboard" className="inline-block mt-6 text-sm text-[#6A0DAD]">← Back to dashboard</Link>
      </div>

      {editing && (
        <EditorModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
      {reportModal && (
        <GenerateReportsModal
          generating={generating}
          onClose={() => setReportModal(false)}
          onGenerate={handleGenerateAll}
        />
      )}
    </div>
  );
}

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
function monthName(m) { return MONTHS[m - 1] || `Month ${m}`; }

function GenerateReportsModal({ generating, onClose, onGenerate }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const submit = (e) => {
    e.preventDefault();
    onGenerate(year, month);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(44,10,94,0.4)", backdropFilter: "blur(4px)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl p-6 md:p-8 w-full max-w-md space-y-4"
        data-testid="generate-reports-modal"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-[#2C0A5E]">Generate monthly reports</h2>
          <button type="button" onClick={onClose} className="text-2xl text-[#6B5E7E]" aria-label="Close">×</button>
        </div>
        <p className="text-sm text-[#6B5E7E]">
          Generates one PDF for <strong>every paid customer</strong> for the month below. Already-generated reports are skipped (idempotent).
        </p>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-[#2C0A5E]">Month</span>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              className="ph-input mt-1"
              data-testid="report-month"
            >
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-[#2C0A5E]">Year</span>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="ph-input mt-1"
              data-testid="report-year"
            >
              {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
        </div>

        <div className="text-xs text-[#6B5E7E] italic">
          This may take ~30 seconds for a few hundred customers. Stays on this page; you&apos;ll get a toast when done.
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-[rgba(106,13,173,0.1)]">
          <button type="button" onClick={onClose} className="ph-btn-ghost" disabled={generating}>Cancel</button>
          <button type="submit" className="ph-btn-primary" disabled={generating} data-testid="generate-confirm">
            {generating ? "Generating…" : `Generate for ${monthName(month)} ${year}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditorModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleArray = (key, val) => {
    const arr = new Set(form[key] || []);
    if (arr.has(val)) arr.delete(val); else arr.add(val);
    setForm((f) => ({ ...f, [key]: Array.from(arr) }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date || !form.theme_text) {
      toast.error("Start date, end date, and theme text are required");
      return;
    }
    onSave(form);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(44,10,94,0.4)", backdropFilter: "blur(4px)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl p-6 md:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto space-y-4"
        data-testid="calendar-editor"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-[#2C0A5E]">{form.id ? "Edit row" : "Add row"}</h2>
          <button type="button" onClick={onClose} className="text-2xl text-[#6B5E7E]" aria-label="Close">×</button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-[#2C0A5E]">Start date *</span>
            <input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className="ph-input mt-1" required data-testid="form-start-date" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-[#2C0A5E]">End date *</span>
            <input type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} className="ph-input mt-1" required data-testid="form-end-date" />
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-[#2C0A5E]">Transit name</span>
            <input type="text" value={form.transit_name || ""} onChange={(e) => set("transit_name", e.target.value)} className="ph-input mt-1" placeholder="e.g. Mercury Retrograde (Libra)" data-testid="form-transit-name" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-[#2C0A5E]">Festival name</span>
            <input type="text" value={form.festival_name || ""} onChange={(e) => set("festival_name", e.target.value)} className="ph-input mt-1" placeholder="e.g. Diwali" data-testid="form-festival-name" />
          </label>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-[#2C0A5E]">Theme text *</span>
          <textarea value={form.theme_text || ""} onChange={(e) => set("theme_text", e.target.value)} rows={3} className="ph-input mt-1" required data-testid="form-theme-text" />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-[#2C0A5E]">Festival significance</span>
          <textarea value={form.festival_significance || ""} onChange={(e) => set("festival_significance", e.target.value)} rows={2} className="ph-input mt-1" data-testid="form-festival-significance" />
        </label>

        <div>
          <span className="text-xs font-medium text-[#2C0A5E] block">Affected Naam Ank (1–9)</span>
          <div className="flex gap-1 mt-1 flex-wrap">
            {[1,2,3,4,5,6,7,8,9].map((n) => {
              const on = (form.affected_naam_ank || []).includes(n);
              return (
                <button key={n} type="button" onClick={() => toggleArray("affected_naam_ank", n)}
                  className="w-9 h-9 rounded-full text-sm"
                  style={{ background: on ? "#6A0DAD" : "white", color: on ? "white" : "#6A0DAD", border: "1px solid #6A0DAD" }}
                  data-testid={`form-naam-ank-${n}`}>
                  {n}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <span className="text-xs font-medium text-[#2C0A5E] block">Affected Rashi</span>
          <div className="flex gap-1.5 mt-1 flex-wrap">
            {RASHI.map((sign) => {
              const on = (form.affected_rashi || []).includes(sign);
              return (
                <button key={sign} type="button" onClick={() => toggleArray("affected_rashi", sign)}
                  className="px-2.5 py-1 rounded-full text-xs"
                  style={{ background: on ? "#6A0DAD" : "white", color: on ? "white" : "#6A0DAD", border: "1px solid #6A0DAD" }}
                  data-testid={`form-rashi-${sign}`}>
                  {sign}
                </button>
              );
            })}
          </div>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-[#2C0A5E]">Affected chakra</span>
          <select value={form.affected_chakra || ""} onChange={(e) => set("affected_chakra", e.target.value)} className="ph-input mt-1" data-testid="form-chakra">
            {CHAKRAS.map((c) => <option key={c} value={c}>{c || "(none)"}</option>)}
          </select>
        </label>

        <div className="flex gap-4 pt-2">
          <label className="flex items-center gap-2 text-sm text-[#2C0A5E]">
            <input type="checkbox" checked={!!form.verified} onChange={(e) => set("verified", e.target.checked)} data-testid="form-verified" />
            Verified (visible to customers)
          </label>
          <label className="flex items-center gap-2 text-sm text-[#2C0A5E]">
            <input type="checkbox" checked={!!form.is_active} onChange={(e) => set("is_active", e.target.checked)} data-testid="form-active" />
            Active
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-[rgba(106,13,173,0.1)]">
          <button type="button" onClick={onClose} className="ph-btn-ghost">Cancel</button>
          <button type="submit" className="ph-btn-primary" data-testid="form-save">Save</button>
        </div>
      </form>
    </div>
  );
}
